import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { EmptyState, PageHeader } from "../../components/admin/AdminUI";
import "../../style/studentpackage.css";
import { BRAND, errorMessage } from "./shared";
import PackageDistribution from "./PackageDistribution";
import PackageConfiguration from "./PackageConfiguration";
import StudentPackageDetail from "./StudentPackageDetail";
import QrScannerModal from "./QrScannerModal";
import DuplicateEmailsModal from "./DuplicateEmailsModal";
import ImportModal from "./ImportModal";

// Halaman admin "Student Package": pembagian paket per student, konfigurasi
// barang per batch, dan scan QR Code student.
const StudentPackagePage = ({ setLocationAdmin }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("distribution");
  const [meta, setMeta] = useState(null);
  const [metaError, setMetaError] = useState("");
  const [scannerOpen, setScannerOpen] = useState(false);
  // { id, initial, fromScan }
  const [detail, setDetail] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  // Email yang dipakai lebih dari satu student (data lama) — hanya laporan.
  const [duplicates, setDuplicates] = useState(null);
  const [duplicatesOpen, setDuplicatesOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const expired = useCallback(() => {
    Swal.fire({
      title: "Session Expired",
      text: "Please login to your account!",
      icon: "warning",
      background: "white",
      color: "black",
      confirmButtonColor: BRAND,
      confirmButtonText: "Ok",
    });
    navigate("/loginAdmin");
  }, [navigate]);

  const loadMeta = useCallback(async () => {
    setMetaError("");
    try {
      const res = await axios.get("/student-package/meta");
      setMeta(res.data);
    } catch (err) {
      if (err?.response?.status === 401) return expired();
      setMetaError(errorMessage(err, "Unable to load Student Package."));
    }
  }, [expired]);

  useEffect(() => {
    if (setLocationAdmin) setLocationAdmin({ pathname: "/studentpackage" });
    axios.get("/meAdmin").catch(() => expired());
    loadMeta();
    axios
      .get("/student-package/duplicate-emails")
      .then((res) => setDuplicates(res.data))
      .catch(() => setDuplicates(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = () => setRefreshKey((n) => n + 1);

  return (
    <div className="adm-main">
      <PageHeader
        title="Student Package"
        subtitle="Record which package items each student has received, and configure the items for each batch."
        actions={
          <>
            <button type="button" className="adm-btn adm-btn--secondary" onClick={() => setImportOpen(true)}>
              <i className="bi bi-file-earmark-arrow-up"></i> Import from Excel
            </button>
            <button type="button" className="adm-btn adm-btn--primary" onClick={() => setScannerOpen(true)}>
              <i className="bi bi-qr-code-scan"></i> Scan Student Package
            </button>
          </>
        }
      />

      {duplicates && duplicates.totalGroups > 0 && (
        <div className="spk-callout is-warning" role="status">
          <i className="bi bi-exclamation-triangle" aria-hidden="true"></i>
          <div>
            <p className="spk-callout-title">
              {duplicates.totalGroups} email address(es) are shared by more than one student
            </p>
            <p className="spk-callout-text">
              Old spreadsheet data — {duplicates.totalStudents} student records are affected. Package records and QR Codes stay with each
              student record, but the student portal can only log in one of them per email. Correct these manually in Edit Student.
            </p>
            <button type="button" className="adm-btn adm-btn--secondary adm-btn--sm" onClick={() => setDuplicatesOpen(true)}>
              Review shared emails
            </button>
          </div>
        </div>
      )}

      <div className="adm-tabs" role="tablist" aria-label="Student Package sections">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "distribution"}
          className={`adm-tab${tab === "distribution" ? " adm-tab--active" : ""}`}
          onClick={() => setTab("distribution")}
        >
          <i className="bi bi-people"></i> Distribution
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "configuration"}
          className={`adm-tab${tab === "configuration" ? " adm-tab--active" : ""}`}
          onClick={() => setTab("configuration")}
        >
          <i className="bi bi-sliders"></i> Configuration
        </button>
      </div>

      {metaError ? (
        <div className="adm-card">
          <EmptyState icon="bi-exclamation-triangle" title="Unable to load" text={metaError} />
          <div className="text-center pb-3">
            <button type="button" className="adm-btn adm-btn--secondary" onClick={loadMeta}>
              Try again
            </button>
          </div>
        </div>
      ) : !meta ? (
        <div className="adm-card">
          <EmptyState icon="bi-hourglass" title="Loading…" />
        </div>
      ) : tab === "distribution" ? (
        <PackageDistribution meta={meta} refreshKey={refreshKey} onOpen={(id) => setDetail({ id, initial: null, fromScan: false })} />
      ) : (
        <PackageConfiguration
          meta={meta}
          onChanged={() => {
            refresh();
            loadMeta();
          }}
        />
      )}

      {scannerOpen && (
        <QrScannerModal
          onClose={() => setScannerOpen(false)}
          onUnauthorized={() => {
            setScannerOpen(false);
            expired();
          }}
          onResolved={(data) => {
            setScannerOpen(false);
            setDetail({ id: data.student.studentRowId, initial: data, fromScan: true });
          }}
        />
      )}

      {importOpen && (
        <ImportModal
          onClose={() => setImportOpen(false)}
          onImported={() => {
            refresh();
            loadMeta();
          }}
        />
      )}

      {duplicatesOpen && duplicates && (
        <DuplicateEmailsModal
          data={duplicates}
          onClose={() => setDuplicatesOpen(false)}
          onOpenStudent={(id) => {
            setDuplicatesOpen(false);
            setDetail({ id, initial: null, fromScan: false });
          }}
        />
      )}

      {detail && (
        <StudentPackageDetail
          key={`${detail.id}-${detail.fromScan}`}
          studentRowId={detail.id}
          initial={detail.initial}
          fromScan={detail.fromScan}
          onClose={() => setDetail(null)}
          onChanged={refresh}
          onScanNext={() => {
            setDetail(null);
            setScannerOpen(true);
          }}
        />
      )}
    </div>
  );
};

export default StudentPackagePage;
